<template>
    <!-- DATE LIBRE — une date et une heure choisies à la main, sans passer par les créneaux.
         Deux colonnes sur desktop : le calendrier à gauche, les horaires à droite, séparés par
         un filet vertical. Le calendrier est celui du projet (vue2-datepicker en mode `inline`,
         même recette que DateRangePicker) : aucune logique de calendrier n'est réécrite ici. -->
    <div class="free-date-v3">
        <div class="free-date-calendar">
            <date-picker
                :value="dateValue"
                @input="onDateInput"
                inline
                :open="true"
                :popup-style="{ display: 'none' }"
                :input-attr="{ style: 'display:none' }"
                :lang="calendarLang"
                type="date"
                format="YYYY-MM-DD"
                class="custom-inline-datepicker"
            />
        </div>

        <div class="free-date-times">
            <!-- Saisie directe : parcourir la liste convient pour une heure ronde, pas pour
                 « 14:05 ». Les deux entrées écrivent la même valeur. -->
            <input v-model="timeInput" type="time" step="300" class="free-date-time-input poppinsSemiBold" />

            <div ref="timeList" class="free-date-time-list">
                <button
                    v-for="slot in timeOptions"
                    :key="slot"
                    type="button"
                    class="free-date-time-cell poppinsMedium"
                    :class="{ selected: slot === time }"
                    :data-time="slot"
                    @click="selectTime(slot)"
                >
                    {{ slot }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'

// Pas de créneau à respecter ici : on propose la journée entière par demi-heures, et la saisie
// directe couvre tout le reste.
const STEP_MINUTES = 30

export default {
    name: 'PlanningFreeDate',
    components: { DatePicker },
    props: {
        // { date: 'YYYY-MM-DD', time: 'HH:mm' } — `null` tant que rien n'est choisi.
        value: { type: Object, default: null },
    },
    data() {
        return {
            date: this.value?.date || moment().format('YYYY-MM-DD'),
            time: this.value?.time || '12:00',
        }
    },
    computed: {
        dateValue() {
            return this.date ? moment(this.date, 'YYYY-MM-DD').toDate() : null
        },
        timeInput: {
            get() {
                return this.time
            },
            set(val) {
                if (!val) return
                this.time = val.substring(0, 5)
                this.emit()
            },
        },
        timeOptions() {
            const options = []

            for (let minutes = 0; minutes < 24 * 60; minutes += STEP_MINUTES) {
                const h = String(Math.floor(minutes / 60)).padStart(2, '0')
                const m = String(minutes % 60).padStart(2, '0')
                options.push(`${h}:${m}`)
            }

            return options
        },
        /**
         * Libellés du calendrier. Repris de DateRangePicker pour que les deux écrans affichent
         * la même semaine (lundi en tête) et les mêmes noms de mois.
         */
        calendarLang() {
            return {
                formatLocale: {
                    firstDayOfWeek: 1,
                    months: [
                        'Janvier',
                        'Février',
                        'Mars',
                        'Avril',
                        'Mai',
                        'Juin',
                        'Juillet',
                        'Août',
                        'Septembre',
                        'Octobre',
                        'Novembre',
                        'Décembre',
                    ],
                    monthsShort: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'],
                    weekdaysMin: ['dim', 'lun', 'mar', 'mer', 'jeu', 'ven', 'sam'],
                },
            }
        },
    },
    mounted() {
        // Première remontée : la date du jour et une heure par défaut valent déjà une réponse,
        // le bouton de validation ne doit pas attendre un clic pour s'activer.
        this.emit()
        this.$nextTick(() => this.scrollToSelected())
    },
    methods: {
        onDateInput(value) {
            if (!value) return

            this.date = moment(value).format('YYYY-MM-DD')
            this.emit()
        },
        selectTime(slot) {
            this.time = slot
            this.emit()
        },
        emit() {
            this.$emit('input', { date: this.date, time: this.time })
        },
        // Ouvrir sur l'heure retenue plutôt qu'à minuit : sans ça, une liste de 48 entrées
        // s'ouvre systématiquement loin de ce qui est sélectionné.
        scrollToSelected() {
            const list = this.$refs.timeList
            if (!list) return

            const cell = list.querySelector(`[data-time="${this.time}"]`)
            if (cell) list.scrollTop = Math.max(0, cell.offsetTop - list.clientHeight / 2)
        },
    },
}
</script>

<style scoped>
.free-date-v3 {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 172px;
    gap: 0;
    min-height: 0;
}

.free-date-calendar {
    padding: 4px 16px 4px 0;
}

/* Filet vertical entre les deux colonnes, comme sur la maquette. */
.free-date-times {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-left: 16px;
    border-left: 1px solid #e9edf3;
    min-height: 0;
}

.free-date-time-input {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid #dfe4ec;
    border-radius: 10px;
    outline: none;
    background: #fff;
    color: #1d1d1b;
    font-size: 15px;
    text-align: center;
    font-variant-numeric: tabular-nums;
}

.free-date-time-input:focus {
    border-color: #7d2dbd;
    box-shadow: 0 0 0 3px rgba(125, 45, 189, 0.12);
}

/* Deux colonnes d'horaires, défilantes : la journée entière tient rarement dans la hauteur. */
.free-date-time-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px;
    overflow-y: auto;
    max-height: 320px;
    padding-right: 4px;
    scrollbar-width: thin;
}

.free-date-time-cell {
    padding: 7px 4px;
    border: 1px solid #e9edf3;
    border-radius: 8px;
    background: #f7f8fa;
    color: #1d1d1b;
    font-size: 13px;
    cursor: pointer;
    transition:
        background 0.18s ease,
        color 0.18s ease,
        border-color 0.18s ease;
    font-variant-numeric: tabular-nums;
}

.free-date-time-cell:hover {
    border-color: #7d2dbd;
    color: #7d2dbd;
}

.free-date-time-cell.selected {
    background: #7d2dbd;
    border-color: #7d2dbd;
    color: #fff;
}

/* Le calendrier inline de vue2-datepicker n'a pas besoin de sa bordure de popup ici. */
.free-date-v3 >>> .custom-inline-datepicker .mx-datepicker-main {
    border: 0;
    box-shadow: none;
}

.free-date-v3 >>> .mx-calendar {
    width: 100%;
    padding: 0;
}

.free-date-v3 >>> .mx-table-date td.active,
.free-date-v3 >>> .mx-table-date td.active:hover {
    background: #7d2dbd;
    border-radius: 8px;
    color: #fff;
}

@media (max-width: 720px) {
    .free-date-v3 {
        grid-template-columns: minmax(0, 1fr);
    }

    .free-date-times {
        padding-left: 0;
        padding-top: 14px;
        border-left: 0;
        border-top: 1px solid #e9edf3;
    }
}
</style>
